import React, { forwardRef, useImperativeHandle } from "react";
import ApiClient from "../../../services/api-client"
import { AgGridReact } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import SimpleTextRenderer from "../aggrid-cell-renderers/simple-text-renderer";
import ActionsRenderer from "../aggrid-cell-renderers/actions-renderer";
import SimpleObjectRenderer from "../aggrid-cell-renderers/simple-object-renderer";
import BranchEditor from "../aggrid-cell-editors/branch-editor";
import DoubleValRenderer from "../aggrid-cell-renderers/doubleval-renderer";
import IntValEditor from "../aggrid-cell-editors/intval-editor";
import useStore from "../../../hooks/useStore";
import { useEditableGrid } from './useEditableGrid';

const EMPTY_ROW = { id: undefined, name: undefined, branch: { value: undefined, label: undefined }, startNumber: undefined };

const isValid = (data) =>
    data.name != undefined && data.name != "" &&
    data.startNumber != undefined &&
    data.branch != undefined && data.branch.value != undefined;

const InvoiceTypeSeriesEditableGrid = forwardRef((props, ref) => {
    const { t } = useTranslation();
    const { hasError, rowStyle, getRowStyle, initGrid, addRow, checkErrors, getItems, setItems, deleteRowLocally } = useEditableGrid({
        isValid,
        emptyRow: EMPTY_ROW,
    });

    const onGridReady = (params) => {
        initGrid(params, []);
    };

    const onCellValueChanged = (params) => {
        const nodeIndex = params.node.rowIndex;
        const rowNode = params.api.getRowNode(nodeIndex);
        const countDisplayedRows = params.api.getDisplayedRowCount() - 1;

        if (nodeIndex == countDisplayedRows && params.newValue != undefined) {
            if (params.column != undefined) {
                const colid = params.column.colId;
                const branchOptions = params.api.columnModel.columnDefs[3].cellRendererParams.options;

                switch (colid) {
                    case "name": {
                        rowNode.setDataValue("startNumber", "0");
                        if (branchOptions.length == 1) {
                            rowNode.setDataValue("branch", { value: branchOptions[0].value, label: branchOptions[0].label });
                        }
                        break;
                    }
                    case "startNumber": {
                        if (branchOptions.length == 1) {
                            rowNode.setDataValue("branch", { value: branchOptions[0].value, label: branchOptions[0].label });
                        }
                        break;
                    }
                }
            }
            addRow(params.api);
            params.api.redrawRows();
        }
        params.api.refreshCells({ rowNodes: [params.node], force: true });
        checkErrors(params.api);
    };

    useImperativeHandle(ref, () => ({
        getData() {
            return getItems();
        },
        hasError() {
            return hasError;
        },
        setData(data) {
            const transformed = data.map(element => {
                element.branch = { value: element.branch.id, label: element.branch.name };
                element.startNumber = element.startNumber.toString();
                return element;
            });
            setItems(transformed);
        }
    }));

    const deleteRow = (params) => {
        const rowData = [];
        const id = params.data.id;
        params.api.forEachNode(node => rowData.push(node.data));
        const companyId = useStore.getState().selectedCompany.id;

        if (id != undefined) {
            ApiClient.deleteCompanySeries(companyId, id).then(
                () => {
                    rowData.splice(params.rowIndex, 1);
                    params.api.setRowData(rowData);
                    toast.success("deleteSucceded");
                },
                error => {
                    toast.error("Error: " + error.response.data.message);
                    console.error(error.response);
                }
            );
        } else {
            deleteRowLocally(params);
        }
    };

    return (
        <div className="row">
            <div style={{ width: '100%', height: '100%' }}>
                <div className="ag-theme-alpine editableSmGrid seriesGrid" style={{ height: 240, width: '100%' }}>
                    <AgGridReact
                        onGridReady={onGridReady}
                        rowStyle={rowStyle}
                        getRowStyle={getRowStyle}
                        style={{ width: '100%', height: '300px' }}
                        onCellValueChanged={onCellValueChanged}
                        suppressRowHoverHighlight={true}
                        stopEditingWhenCellsLoseFocus={true}
                        borders={true}
                        singleClickEdit={true}
                        columnDefs={[
                            { field: "logofile", hide: true },
                            { field: "name", flex: 1, minWidth: 200, headerName: t("SeriesName"), headerClass: "required", sortable: true, editable: true, cellRenderer: SimpleTextRenderer },
                            { field: "startNumber", flex: 1, minWidth: 200, headerName: t("startNumber"), headerClass: "required", editable: true, cellEditor: IntValEditor, cellRenderer: DoubleValRenderer, cellRendererParams: { precision: 0 } },
                            { field: "branch", flex: 2, minWidth: 250, headerName: t("Branch"), sortable: true, editable: true, cellRenderer: SimpleObjectRenderer, cellEditor: BranchEditor, cellRendererParams: { options: props.optionBranches } },
                            { field: "action", flex: 1, minWidth: 100, headerName: t("Actions"), cellRenderer: ActionsRenderer, cellRendererParams: { deleteRow: deleteRow } }
                        ]}
                    >
                    </AgGridReact>
                </div>
            </div>
        </div>
    );
});

export default InvoiceTypeSeriesEditableGrid;
